import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L, { Map as LeafletMap, Marker as LeafletMarker } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './StoreLocator.css';

// Dynamic store marker icon based on brands carried
const getStoreMarkerIcon = (store: { brands?: { red_bridge: boolean; tiny_dinosaur: boolean }; is_headquarters?: boolean }) => {
  const hasRB = store.brands?.red_bridge;
  const hasTD = store.brands?.tiny_dinosaur;
  const hasBoth = hasRB && hasTD;
  const hasNone = !hasRB && !hasTD;
  const isHQ = store.is_headquarters;

  // Headquarters gets a simple gold star marker (no logos)
  if (isHQ) {
    return L.divIcon({
      className: 'custom-store-marker',
      html: '<div class="headquarters-marker"><span class="hq-star">⭐</span></div>',
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    });
  }

  // Regular stores show brand logos
  let html = '<div class="store-marker">';
  if (hasRB) html += '<img src="/rbf-logo.png" class="marker-logo" alt="RBF" />';
  if (hasTD) html += '<img src="/tdf-logo.png" class="marker-logo" alt="TDF" />';
  if (hasNone) html += '<span class="marker-fallback">🏪</span>';
  html += '</div>';

  // Calculate icon size based on content:
  // Logo: 20px, Padding: 3px*2=6px, Border: 2px*2=4px
  // Single logo: 20+6+4 = 30px wide
  // Two logos: 20+2(gap)+20+6+4 = 52px wide
  const width = hasBoth ? 52 : 30;
  const height = 30;

  return L.divIcon({
    className: 'custom-store-marker',
    html: html,
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height],
  });
};

// User location marker (blue dot style)
const userLocationIcon = L.divIcon({
  className: 'user-location-marker',
  html: '<div class="user-marker-dot"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// API_BASE should NOT include /api - we add it in the fetch call
const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.redbridgefeed.com').replace(/\/api\/?$/, '');

interface StoreCategory {
  emoji: string;
  label: string;
}

interface AnimalType {
  key: string;
  label: string;
  emoji: string;
}

interface StoreBrands {
  red_bridge: boolean;
  tiny_dinosaur: boolean;
}

interface Store {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  hours: string;
  description: string;
  website: string;
  logo_url: string | null;
  latitude: number;
  longitude: number;
  distance_miles: number | null;
  categories: StoreCategory[];
  brands: StoreBrands;
  animal_types: AnimalType[];
  is_headquarters?: boolean;
}

interface StoreLocatorResponse {
  stores: Store[];
  count: number;
  user_location: {
    lat: number | null;
    lng: number | null;
    from_zip: string | null;
  };
  available_filters: StoreCategory[];
}

// Component to recenter map when user location changes
function MapController({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

// Component to expose map instance to parent
function MapInstanceSetter({ mapRef }: { mapRef: React.MutableRefObject<LeafletMap | null> }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  return null;
}

export default function StoreLocator() {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [zipCode, setZipCode] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [availableFilters, setAvailableFilters] = useState<StoreCategory[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied'>('idle');
  const [mapType, setMapType] = useState<'map' | 'satellite'>('map');

  // Ref for store list container (for scroll-to-card)
  const storeListRef = useRef<HTMLDivElement>(null);
  const storeCardRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  // Ref for map markers (for opening popups programmatically)
  const markerRefs = useRef<{ [key: number]: LeafletMarker | null }>({});
  // Ref for map instance (for flyTo)
  const mapRef = useRef<LeafletMap | null>(null);

  // Default center: Eastern Washington
  const defaultCenter: [number, number] = [47.5, -117.5];
  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultCenter);
  const [mapZoom, setMapZoom] = useState(7);

  const fetchStores = useCallback(async (lat?: number, lng?: number, zip?: string, category?: string, brand?: string) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (lat && lng) {
        params.append('lat', lat.toString());
        params.append('lng', lng.toString());
      }
      if (zip) params.append('zip', zip);
      if (category) params.append('category', category);
      if (brand) params.append('brand', brand);

      const url = `${API_BASE}/api/store-locator/?${params}`;
      console.log('[StoreLocator] Fetching:', url);

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch stores');

      const data: StoreLocatorResponse = await response.json();
      console.log('[StoreLocator] Response:', {
        user_location: data.user_location,
        store_count: data.stores.length,
        first_3_stores: data.stores.slice(0, 3).map(s => ({
          name: s.name,
          distance: s.distance_miles
        }))
      });

      setStores(data.stores);
      setAvailableFilters(data.available_filters);

      // Update user location from geocoded ZIP if provided
      if (data.user_location.lat && data.user_location.lng) {
        setUserLocation({ lat: data.user_location.lat, lng: data.user_location.lng });
        setMapCenter([data.user_location.lat, data.user_location.lng]);
        setMapZoom(10);
      } else if (data.stores.length > 0) {
        setMapCenter([data.stores[0].latitude, data.stores[0].longitude]);
        setMapZoom(8);
      }
    } catch (err) {
      setError('Unable to load stores. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Request user location on mount
  useEffect(() => {
    setLocationStatus('requesting');

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setLocationStatus('granted');
          fetchStores(latitude, longitude, undefined, selectedCategory, selectedBrand);
        },
        (err) => {
          console.log('Geolocation denied or unavailable:', err.message);
          setLocationStatus('denied');
          fetchStores(); // Fetch without location
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    } else {
      setLocationStatus('denied');
      fetchStores();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when category or brand filter changes
  useEffect(() => {
    if (locationStatus !== 'requesting') {
      fetchStores(userLocation?.lat, userLocation?.lng, zipCode || undefined, selectedCategory || undefined, selectedBrand || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedBrand]);

  const handleZipSearch = () => {
    if (zipCode.length >= 5) {
      fetchStores(undefined, undefined, zipCode, selectedCategory || undefined, selectedBrand || undefined);
    }
  };

  const getDirectionsUrl = (store: Store) => {
    const address = `${store.address}, ${store.city}, ${store.state} ${store.zip}`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  const getCallUrl = (phone: string) => {
    return `tel:${phone.replace(/[^0-9+]/g, '')}`;
  };

  // Fly to store and open popup after animation completes
  const flyToStoreAndOpenPopup = useCallback((store: Store) => {
    const map = mapRef.current;
    const marker = markerRefs.current[store.id];

    if (map) {
      // Use flyTo for smooth animation to center the store
      map.flyTo([store.latitude, store.longitude], 14, {
        duration: 0.5, // 500ms animation
        easeLinearity: 0.25
      });

      // Open popup after flyTo animation completes
      const openPopupAfterMove = () => {
        if (marker) {
          marker.openPopup();
        }
        map.off('moveend', openPopupAfterMove);
      };

      map.on('moveend', openPopupAfterMove);

      // Fallback: open popup after 600ms if moveend doesn't fire
      setTimeout(() => {
        map.off('moveend', openPopupAfterMove);
        if (marker) {
          marker.openPopup();
        }
      }, 600);
    } else if (marker) {
      // Fallback if map ref not available
      marker.openPopup();
    }
  }, []);

  // Scroll to store card when marker is clicked
  const scrollToStoreCard = (store: Store) => {
    setSelectedStore(store);

    // Scroll to the store card - position at top with small offset
    const cardElement = storeCardRefs.current[store.id];
    if (cardElement && storeListRef.current) {
      const listContainer = storeListRef.current;
      const cardTop = cardElement.offsetTop - listContainer.offsetTop;
      const offset = 16; // 16px offset from top

      listContainer.scrollTo({
        top: cardTop - offset,
        behavior: 'smooth'
      });
    }

    // Fly to store and open popup (marker click already centers, but ensure popup opens)
    flyToStoreAndOpenPopup(store);
  };

  // Handle card click - fly map to store (centered) and open popup
  const handleCardClick = (store: Store) => {
    setSelectedStore(store);

    // Fly to store location (centered) and open popup after animation
    flyToStoreAndOpenPopup(store);
  };

  // Handle "Use My Location" button click
  const handleUseMyLocation = () => {
    setLocationStatus('requesting');

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setLocationStatus('granted');
          setZipCode(''); // Clear ZIP since we're using GPS
          fetchStores(latitude, longitude, undefined, selectedCategory, selectedBrand);
        },
        (err) => {
          console.log('Geolocation error:', err.message);
          setLocationStatus('denied');
          alert('Unable to get your location. Please enter a ZIP code instead.');
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      setLocationStatus('denied');
      alert('Geolocation is not supported by your browser.');
    }
  };

  // Tile layer URLs
  const mapTileUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const satelliteTileUrl = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

  return (
    <div className="store-locator">
      <div className="store-locator-header">
        <h1>Find a Store</h1>
        <p>Find Red Bridge Farm Feed at a store near you</p>
      </div>

      <div className="store-locator-controls">
        {/* Zip Code Search */}
        <div className="zip-search">
          <input
            type="text"
            placeholder="Enter ZIP code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyPress={(e) => e.key === 'Enter' && handleZipSearch()}
          />
          <button
            type="button"
            className="location-btn"
            onClick={handleUseMyLocation}
            disabled={locationStatus === 'requesting'}
            title="Use my location"
          >
            {locationStatus === 'requesting' ? '⏳' : '📍'}
          </button>
          <button onClick={handleZipSearch} disabled={zipCode.length < 5}>
            Search
          </button>
        </div>

        {/* Brand Filter */}
        <div className="brand-filter">
          <select
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
          >
            <option value="">All Brands</option>
            <option value="redbridge">Red Bridge Farm Feed</option>
            <option value="tinydino">Tiny Dinosaur Feed</option>
          </select>
        </div>

        {/* Category Filter */}
        {availableFilters.length > 0 && (
          <div className="category-filter">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Animals</option>
              {availableFilters.map((cat) => (
                <option key={cat.label} value={cat.label.toLowerCase()}>
                  {cat.emoji} {cat.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Location Status */}
        {locationStatus === 'denied' && !zipCode && (
          <p className="location-hint">
            Enter your ZIP code to find stores near you
          </p>
        )}
      </div>

      <div className="store-locator-content">
        {/* Map */}
        <div className="store-locator-map">
          {/* Map Type Toggle */}
          <div className="map-type-toggle">
            <button
              className={`map-toggle-btn ${mapType === 'map' ? 'active' : ''}`}
              onClick={() => setMapType('map')}
            >
              Map
            </button>
            <button
              className={`map-toggle-btn ${mapType === 'satellite' ? 'active' : ''}`}
              onClick={() => setMapType('satellite')}
            >
              Satellite
            </button>
          </div>

          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
          >
            {/* Conditional Tile Layer */}
            {mapType === 'map' ? (
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url={mapTileUrl}
              />
            ) : (
              <TileLayer
                attribution='&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url={satelliteTileUrl}
              />
            )}
            <MapController center={mapCenter} zoom={mapZoom} />
            <MapInstanceSetter mapRef={mapRef} />

            {/* User location marker */}
            {userLocation && (
              <Marker
                position={[userLocation.lat, userLocation.lng]}
                icon={userLocationIcon}
              >
                <Popup>Your Location</Popup>
              </Marker>
            )}

            {/* Store markers with brand logos */}
            {stores.map((store) => (
              <Marker
                key={store.id}
                position={[store.latitude, store.longitude]}
                icon={getStoreMarkerIcon(store)}
                ref={(ref) => { markerRefs.current[store.id] = ref; }}
                eventHandlers={{
                  click: () => scrollToStoreCard(store),
                }}
              >
                <Popup>
                  <div className="map-popup">
                    <strong>{store.name}</strong>
                    <br />
                    {store.city}, {store.state}
                    {store.distance_miles && (
                      <>
                        <br />
                        <em>{store.distance_miles} miles</em>
                      </>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Store List */}
        <div className="store-locator-list" ref={storeListRef}>
          {loading ? (
            <div className="loading">Loading stores...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : stores.length === 0 ? (
            <div className="no-stores">
              No stores found{selectedCategory && ` carrying ${selectedCategory} feed`}.
              Try a different location or filter.
            </div>
          ) : (
            <>
              <p className="store-count">{stores.length} store{stores.length !== 1 ? 's' : ''} found</p>
              {stores.map((store) => (
                <div
                  key={store.id}
                  ref={(el) => { storeCardRefs.current[store.id] = el; }}
                  className={`store-card ${selectedStore?.id === store.id ? 'selected' : ''}`}
                  onClick={() => handleCardClick(store)}
                >
                  {/* Card Header - Brand Logos + Title */}
                  <div className="store-card-header">
                    {/* Brand Logos */}
                    <div className="store-brand-logos">
                      {store.brands?.red_bridge && (
                        <img
                          src="/rbf-logo.png"
                          alt="Red Bridge Farm Feed"
                          className="brand-logo"
                          title="Carries Red Bridge Farm Feed"
                        />
                      )}
                      {store.brands?.tiny_dinosaur && (
                        <img
                          src="/tdf-logo.png"
                          alt="Tiny Dinosaur Feed"
                          className="brand-logo"
                          title="Carries Tiny Dinosaur Feed"
                        />
                      )}
                      {!store.brands?.red_bridge && !store.brands?.tiny_dinosaur && (
                        <div className="store-logo-placeholder">🏪</div>
                      )}
                    </div>

                    {/* Store Name & Distance */}
                    <div className="store-card-info">
                      <h3 className="store-name">
                        {store.is_headquarters && <span className="headquarters-star">⭐</span>}
                        {store.name}
                      </h3>
                      {store.distance_miles !== null && (
                        <span className="store-distance">{store.distance_miles} mi</span>
                      )}
                    </div>
                  </div>

                  {/* Headquarters Badge */}
                  {store.is_headquarters && (
                    <div className="headquarters-badge">
                      HEADQUARTERS - Mill Location
                    </div>
                  )}

                  {/* Address */}
                  <div className="store-address">
                    <p>{store.address}</p>
                    <p>{store.city}, {store.state} {store.zip}</p>
                  </div>

                  {/* Animal Types (from manual checkboxes) */}
                  {store.animal_types && store.animal_types.length > 0 && (
                    <div className="store-animal-types">
                      {store.animal_types.map((animal) => (
                        <span key={animal.key} className="animal-type-badge">
                          {animal.emoji} {animal.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Animal Categories (from order history) */}
                  {store.categories.length > 0 && (
                    <div className="store-categories">
                      {store.categories.map((cat) => (
                        <span key={cat.label} className="category-badge">
                          {cat.emoji} {cat.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons - Icons only on mobile, text on desktop */}
                  <div className="store-actions">
                    <a
                      href={getDirectionsUrl(store)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-directions"
                      onClick={(e) => e.stopPropagation()}
                      title="Get Directions"
                    >
                      <span className="btn-icon">🧭</span>
                      <span className="btn-text">Directions</span>
                    </a>
                    {store.phone && (
                      <a
                        href={getCallUrl(store.phone)}
                        className="btn btn-call"
                        onClick={(e) => e.stopPropagation()}
                        title="Call Store"
                      >
                        <span className="btn-icon">📞</span>
                        <span className="btn-text">Call</span>
                      </a>
                    )}
                    {store.website && (
                      <a
                        href={store.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-website"
                        onClick={(e) => e.stopPropagation()}
                        title="Visit Website"
                      >
                        <span className="btn-icon">🌐</span>
                        <span className="btn-text">Website</span>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
